import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Shield } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import {
  useGetCapitalQuery,
  useGetClanMembersQuery,
  useGetClanQuery,
  useGetCurrentWarQuery,
  useGetCwlQuery,
} from '@/services/qclansApi';
import { classifyQclansError, qclansErrorKey } from '@/services/qclansErrors';
import { formatDateShort } from '@/lib/format';
import { cn } from '@/lib/cn';

type Tab = 'members' | 'war' | 'cwl' | 'capital';

/**
 * Clan detail card + members + war/CWL/capital tabs. Each tab fetches lazily
 * (skipped until selected) so the clan view loads fast and the war/CWL polls
 * don't fire unless the user opens them.
 *
 * Honest error handling per the contract:
 *   - war board → 403 `war_log_private` renders the "make war log public" hint.
 *   - CWL       → 404 `not_in_cwl` renders the off-season hint.
 */
export function CocClanDetail({ tag, fromProfile }: { tag: string; fromProfile?: boolean }) {
  const { t } = useTranslation('coc');
  const haptics = useHaptics();
  const [tab, setTab] = useState<Tab>('members');

  const clanQuery = useGetClanQuery(tag);
  const clan = clanQuery.data;

  if (clanQuery.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (clanQuery.error || !clan) {
    return (
      <div className="px-4 py-8 text-center text-sm text-danger" role="alert">
        {clanQuery.error
          ? t(qclansErrorKey(classifyQclansError(clanQuery.error)))
          : t('clan.loadError')}
      </div>
    );
  }

  const tabs: Tab[] = ['members', 'war', 'cwl', 'capital'];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      {/* Clan header */}
      <header className="flex items-center gap-3 rounded-xl bg-bg-secondary p-4">
        {clan.badgeUrls?.medium ? (
          <img src={clan.badgeUrls.medium} alt="" className="h-14 w-14 shrink-0" />
        ) : (
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Shield className="h-7 w-7" />
          </span>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          {fromProfile && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-brand">
              {t('clan.fromProfile')}
            </span>
          )}
          <h1 className="truncate text-lg font-bold text-fg">{clan.name}</h1>
          <span className="text-[11px] tabular-nums text-fg-muted">{clan.tag}</span>
          <span className="mt-0.5 text-[11px] text-fg-muted">
            {t('clan.level', { level: clan.clanLevel })} · {t('clan.members', { count: clan.members })}
          </span>
        </div>
      </header>

      {/* Quick facts */}
      <section className="grid grid-cols-2 gap-2">
        <Fact label={t('clan.points')} value={clan.clanPoints.toLocaleString()} />
        <Fact
          label={t('clan.warRecord')}
          value={t('clan.warRecordValue', {
            wins: clan.warWins,
            ties: clan.warTies ?? 0,
            losses: clan.warLosses ?? 0,
          })}
        />
        {clan.warLeague && <Fact label={t('clan.warLeague')} value={clan.warLeague.name} />}
      </section>

      {/* Tabs */}
      <div className="flex gap-1 rounded-xl bg-bg-secondary p-1">
        {tabs.map((tb) => (
          <button
            key={tb}
            type="button"
            onClick={() => {
              haptics.selection();
              setTab(tb);
            }}
            className={cn(
              'flex-1 rounded-lg px-2 py-2 text-xs font-bold transition-colors',
              tb === tab ? 'bg-brand text-brand-fg' : 'text-fg-muted',
            )}
          >
            {t(`clan.tabs.${tb}`)}
          </button>
        ))}
      </div>

      {tab === 'members' && <MembersTab tag={tag} />}
      {tab === 'war' && <WarTab tag={tag} isWarLogPublic={clan.isWarLogPublic} />}
      {tab === 'cwl' && <CwlTab tag={tag} />}
      {tab === 'capital' && <CapitalTab tag={tag} />}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-bg-secondary p-3">
      <p className="text-[10px] uppercase tracking-wide text-fg-muted">{label}</p>
      <p className="mt-0.5 text-sm font-bold text-fg">{value}</p>
    </div>
  );
}

/* ── Members ──────────────────────────────────────────────────────────── */

function MembersTab({ tag }: { tag: string }) {
  const { t } = useTranslation('coc');
  const query = useGetClanMembersQuery(tag);
  const members = query.data?.items ?? [];

  if (query.isLoading) return <Loader />;
  if (query.error) {
    return (
      <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
        {t(qclansErrorKey(classifyQclansError(query.error)))}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl bg-bg-secondary">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-[11px] uppercase tracking-wide text-fg-muted">
            <th className="px-2 py-2 text-left font-semibold">{t('clan.membersTab.rank')}</th>
            <th className="px-2 py-2 text-left font-semibold">{t('clan.membersTab.name')}</th>
            <th className="px-2 py-2 text-right font-semibold">{t('clan.membersTab.trophies')}</th>
            <th className="px-2 py-2 text-right font-semibold">{t('clan.membersTab.donations')}</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.tag} className="border-b border-border last:border-0">
              <td className="px-2 py-2 font-mono text-fg-muted">{m.clanRank}</td>
              <td className="px-2 py-2">
                <span className="font-semibold text-fg">{m.name}</span>
                <span className="block text-[10px] capitalize text-fg-muted">{m.role}</span>
              </td>
              <td className="px-2 py-2 text-right font-mono text-fg">{m.trophies.toLocaleString()}</td>
              <td className="px-2 py-2 text-right font-mono text-fg-muted">{m.donations.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── War ──────────────────────────────────────────────────────────────── */

function WarTab({ tag, isWarLogPublic }: { tag: string; isWarLogPublic: boolean }) {
  const { t } = useTranslation('coc');
  // Skip the call entirely when the clan flag says the war log is private —
  // saves a guaranteed 403 round-trip. Still handle a runtime 403 below in case
  // the flag lags the live state.
  const query = useGetCurrentWarQuery(tag, { skip: !isWarLogPublic });

  if (!isWarLogPublic) return <WarLogPrivate />;
  if (query.isLoading) return <Loader />;
  if (query.error) {
    const kind = classifyQclansError(query.error);
    if (kind === 'war_log_private') return <WarLogPrivate />;
    return (
      <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
        {t(qclansErrorKey(kind))}
      </p>
    );
  }

  const war = query.data;
  if (!war || war.state === 'notInWar') {
    return <p className="py-6 text-center text-sm text-fg-muted">{t('clan.war.noWar')}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between rounded-xl bg-bg-secondary px-3 py-2 text-xs">
        <span className="font-semibold capitalize text-fg">{war.state}</span>
        {war.teamSize != null && (
          <span className="text-fg-muted">{t('clan.war.teamSize', { n: war.teamSize })}</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <WarSide label={t('clan.war.yourClan')} name={war.clan.name} stars={war.clan.stars} destruction={war.clan.destructionPercentage} />
        <WarSide label={t('clan.war.opponent')} name={war.opponent.name} stars={war.opponent.stars} destruction={war.opponent.destructionPercentage} />
      </div>
    </div>
  );
}

function WarSide({
  label,
  name,
  stars,
  destruction,
}: {
  label: string;
  name: string;
  stars: number;
  destruction: number;
}) {
  const { t } = useTranslation('coc');
  return (
    <div className="rounded-xl bg-bg-secondary p-3 text-center">
      <p className="text-[10px] uppercase tracking-wide text-fg-muted">{label}</p>
      <p className="mt-0.5 truncate text-sm font-bold text-fg">{name}</p>
      <p className="mt-1 text-lg font-bold tabular-nums text-brand">
        ⭐ {stars}
      </p>
      <p className="text-[11px] tabular-nums text-fg-muted">
        {destruction.toFixed(1)}% {t('clan.war.destruction')}
      </p>
    </div>
  );
}

function WarLogPrivate() {
  const { t } = useTranslation('coc');
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl bg-bg-secondary p-5 text-center">
      <Lock aria-hidden className="h-8 w-8 text-fg-muted" />
      <p className="text-sm font-semibold text-fg">{t('clan.war.privateTitle')}</p>
      <p className="text-[12px] leading-snug text-fg-muted">{t('clan.war.privateBody')}</p>
    </div>
  );
}

/* ── CWL ──────────────────────────────────────────────────────────────── */

function CwlTab({ tag }: { tag: string }) {
  const { t } = useTranslation('coc');
  const query = useGetCwlQuery(tag);

  if (query.isLoading) return <Loader />;
  if (query.error) {
    const kind = classifyQclansError(query.error);
    if (kind === 'not_in_cwl' || kind === 'not_found') {
      return (
        <div className="flex flex-col items-center gap-3 rounded-xl bg-bg-secondary p-5 text-center">
          <p className="text-sm font-semibold text-fg">{t('clan.cwl.notInTitle')}</p>
          <p className="text-[12px] leading-snug text-fg-muted">{t('clan.cwl.notInBody')}</p>
        </div>
      );
    }
    return (
      <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
        {t(qclansErrorKey(kind))}
      </p>
    );
  }

  const group = query.data;
  if (!group) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between rounded-xl bg-bg-secondary px-3 py-2 text-xs">
        <span className="font-semibold text-fg">{t('clan.cwl.season', { season: group.season })}</span>
        <span className="text-fg-muted">{t('clan.cwl.rounds', { count: group.rounds.length })}</span>
      </div>
      <h3 className="px-1 text-[11px] font-bold uppercase tracking-wider text-fg-muted">
        {t('clan.cwl.groupClans')}
      </h3>
      <ul className="flex flex-col gap-1.5">
        {group.clans.map((c) => (
          <li key={c.tag} className="flex items-center gap-2 rounded-xl bg-bg-secondary p-2.5">
            {c.badgeUrls?.small ? (
              <img src={c.badgeUrls.small} alt="" className="h-7 w-7" />
            ) : (
              <span className="flex h-7 w-7 items-center justify-center rounded bg-brand/15 text-brand">
                <Shield className="h-3.5 w-3.5" />
              </span>
            )}
            <span className="min-w-0 flex-1 truncate text-sm text-fg">{c.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Capital ──────────────────────────────────────────────────────────── */

function CapitalTab({ tag }: { tag: string }) {
  const { t } = useTranslation('coc');
  const query = useGetCapitalQuery(tag);

  if (query.isLoading) return <Loader />;
  if (query.error) {
    return (
      <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
        {t(qclansErrorKey(classifyQclansError(query.error)))}
      </p>
    );
  }

  const seasons = query.data?.items ?? [];
  if (seasons.length === 0) {
    return <p className="py-6 text-center text-sm text-fg-muted">{t('clan.capital.noSeasons')}</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {seasons.map((s, idx) => (
        <li key={`${s.endTime}-${idx}`} className="rounded-xl bg-bg-secondary p-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
              {t('clan.capital.season')}
            </span>
            <span className="text-[11px] text-fg-muted">{formatDateShort(s.endTime)}</span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-fg-muted">
                {t('clan.capital.totalLoot')}
              </p>
              <p className="text-sm font-bold tabular-nums text-fg">
                {s.capitalTotalLoot.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-fg-muted">
                {t('clan.capital.raids')}
              </p>
              <p className="text-sm font-bold tabular-nums text-fg">{s.raidsCompleted}</p>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
